<template>
  <main role="main" class="py-4">
    <b-container class="container-x" style="max-width: 800px;">
      <!-- Title Section -->
      <div class="d-flex flex-row align-items-center justify-content-between mb-4 border-bottom pb-2">
        <h4 class="m-0 text-dark font-weight-bold">{{ $t("updateApiConfig") }}</h4>
      </div>

      <!-- Config Card -->
      <b-card class="shadow-sm border">
        <div v-if="isLoading" class="py-5 text-center">
          <b-spinner variant="primary"></b-spinner>
          <div class="text-muted mt-2 small">{{ $t("loadingData") }}</div>
        </div>

        <b-form v-if="!isLoading" class="mt-2" @submit.prevent="submitForm">
  
          <b-form-row>
            <b-form-group class="col-lg-12 mb-3" label-class="font-weight-bold text-secondary small">
              <template v-slot:label>
                URL <span class="text-danger">*</span>
              </template>
              <b-form-input type="text" minlength="1" v-model="apiUrl" />
            </b-form-group>
          </b-form-row>
          <b-form-row>
            <b-form-group class="col-lg-12 mb-3" label-class="font-weight-bold text-secondary small">
              <template v-slot:label>
                Secret Key
              </template>
              <b-form-input type="text" required minlength="2" v-model="apiSecret" />
            </b-form-group>
          </b-form-row>  
          <b-form-row>
            <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
              <template v-slot:label>
                {{ $t("maximumNumberOfParticipantsInMeeting") }} <span class="text-danger">*</span>
              </template>
              <b-form-input required type="number" min="2" max="1000" v-model="maxParticipants" />
            </b-form-group>
            <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
              <b-form-radio-group buttons button-variant="outline-primary" class="w-100"
                                  v-model="maxParticipants"
                                  :options="maximumNumberOfParticipantsInMeetingOptions" />
            </b-form-group>
          </b-form-row>
  
          <b-form-row>
            <b-form-group class="col-lg-5 mb-3" label-class="font-weight-bold text-secondary small">
              <template v-slot:label>
                {{ $t("numberOfMeetingsPerMonth") }} <span class="text-danger">*</span>
              </template>
              <b-form-input required type="number" min="1" max="100000" v-model="maxMeetings" />
            </b-form-group>
            <b-form-group class="col-lg-7 mb-3" label="." label-class="text-white small">
              <b-form-radio-group buttons button-variant="outline-primary" class="w-100"
                                  v-model="maxMeetings" :options="numberOfMeetingsPerMonthOptions" />
            </b-form-group>
          </b-form-row>

          <b-form-row class="mb-3">
            <b-form-group class="col-lg-12 mb-0">
              <div class="d-flex flex-row align-items-center">
                <span class="font-weight-bold text-secondary small mr-2">{{ $t('activeStatus') }}</span>
                <b-form-radio-group required :options="activeOptions" v-model="status" inline />
              </div>
            </b-form-group>
          </b-form-row>
  
          <!-- Footer Area -->
          <div class="d-flex flex-row align-items-center justify-content-end border-top pt-3 mt-4">
            <b-button type="button" variant="outline-secondary" class="mr-2" style="min-width: 120px;" @click="goBackToList">
              {{ $t("cancel") || 'Hủy' }}
            </b-button>
            <b-button type="submit" variant="primary" :disabled="isSubmitting" style="min-width: 140px;">
              <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
              {{ $t("saveChanges") }}
            </b-button>
          </div>
        </b-form>
      </b-card>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
.container-x {
  max-width: 800px;
}
.card {
  border-radius: 8px;
  background-color: #f4f9ff;
}
.font-weight-bold {
  font-weight: 600 !important;
}
</style>
  
<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import _ from "lodash";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import BaseComponent from "@/components/BaseComponent";

@Component
export default class EditApiConfig extends BaseComponent {

    id: number;
    apiUrl: any;
    apiSecret: any;
    coreType: any;
    description: any;
    maxParticipants: number = null;;
    maxMeetings: number = null;;
    status: number = 1;
    packageStatusOptions = [
        { value: 1, text: this.$t("demo").toString() },
        { value: 2, text: this.$t("trial").toString() },
        { value: 3, text: this.$t("testing").toString() },
        { value: 4, text: this.$t("official").toString() },
    ];

    maximumNumberOfParticipantsInMeetingOptions = [50, 100, 300, 500, 1000];
    maximumNumberOfParticipantsInMeeting: number = null;

    numberOfMeetingsPerMonthOptions = [100, 300, 500, 1000];
    numberOfMeetingsPerMonth: number = null;

    price: number = null;

    activeOptions = [
        { text: this.$t("yes").toString(), value: 1 },
        { text: this.$t("no").toString(), value: 0 },
    ];

    isLoading = false;
    isSubmitting = false;


    mounted() {
        this.id = +this.$route.params.id;
        this.fetchPlan();
    }


    async fetchPlan() {
        try {
            this.isLoading = true;
            const apiConfig = await subscriptionPlanService.detailCorePlanType(this.id);

            this.apiUrl = apiConfig.apiUrl.trim();
            this.apiSecret = apiConfig.apiSecret.trim();
            this.coreType =  apiConfig.coreType.trim();
            this.description = apiConfig.description.trim();
            this.maxParticipants = apiConfig.maxParticipants;
            this.maxMeetings = apiConfig.maxMeetings;
            this.status = apiConfig.active;

        } catch (e) {
            this.toastError(e);

        } finally {
            this.isLoading = false;
        }
    }

    async submitForm() {
        try {
            this.isSubmitting = true;

            const data = {
              id: this.id,
              apiUrl: this.apiUrl,
              apiSecret: this.apiSecret,
              coreType: this.coreType,
              description: this.description,
              maxParticipants: +this.maxParticipants,
              maxMeetings: +this.maxMeetings,
              active :this.status
            };

            await subscriptionPlanService.updateCorePlanType(data);
            this.goBackToList();
            this.toastSuccessDelay(this.$t("updateApiConfigSuccessful").toString());
        } catch (e) {
            this.toastError(e);

        } finally {
            this.isSubmitting = false;
        }
    }

    goBackToList() {
        this.$router.push("/admin-settings/manage-config");
    }

}
</script>
  